/** Configuration — Case Actions — catalogues, conditions, sample rules */

export const CASE_ACTIONS_ROUTE = '/admin/configuration/case-actions';

export const CASE_ACTION_MODULE_OPTIONS = [
  { value: 'moderation', label: 'Moderation' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'fraud_security', label: 'Fraud & Security' },
  { value: 'appeals', label: 'Appeals' },
  { value: 'support_tickets', label: 'Support Tickets' },
];

export const RECORD_TYPES_BY_MODULE = {
  moderation: [
    { value: 'moderation_case', label: 'Moderation Case' },
  ],
  compliance: [
    { value: 'compliance_case', label: 'Compliance Case' },
  ],
  fraud_security: [
    { value: 'fraud_security_case', label: 'Fraud & Security Case' },
  ],
  appeals: [
    { value: 'appeal', label: 'Appeal' },
  ],
  support_tickets: [
    { value: 'support_ticket', label: 'Support Ticket' },
  ],
};

/** Supported selectable Action Types. Excluded: Start Case/Review, assign/reassign, Apply Restriction, F&S/Compliance retries, Archive actions, deferred Support→F&S creation. */
export const ACTION_TYPE_OPTIONS = [
  { value: 'decision_form_save', label: 'Decision Form Save' },
  { value: 'save_internal_note', label: 'Save Internal Note' },
  { value: 'approve', label: 'Approve' },
  { value: 'not_approve', label: 'Not Approve' },
  { value: 'ask_more_info', label: 'Ask More Info' },
  { value: 'transfer', label: 'Transfer' },
  { value: 'transfer_to_fraud_security', label: 'Send to Fraud & Security' },
  { value: 'sign_out_active_sessions', label: 'Sign Out Active Sessions' },
  { value: 'link_record', label: 'Link Record' },
  { value: 'add_evidence', label: 'Save Added Evidence' },
  { value: 'confirm_decision', label: 'Confirm Decision' },
  { value: 'send_message', label: 'Staff Send Message' },
  { value: 'save_confirm_decision', label: 'Save and Confirm Decision' },
  { value: 'save_confirm_correction', label: 'Save and Confirm Correction' },
  { value: 'retry_action', label: 'Retry Action' },
  { value: 'send_reply', label: 'Send Reply' },
  { value: 'mark_in_progress', label: 'Mark In Progress' },
  { value: 'mark_awaiting_reply', label: 'Mark Awaiting Reply' },
  { value: 'mark_resolved', label: 'Mark Resolved' },
  { value: 'return_to_in_progress', label: 'Return to In Progress' },
  // Legacy labels retained for older rows / Decision Form button effects
  { value: 'submit_rejection', label: 'Not Approve' },
  { value: 'request_more_information', label: 'Ask More Info' },
  { value: 'request_information', label: 'Ask More Info' },
  { value: 'hide', label: 'Hide' },
  { value: 'restore_content', label: 'Restore Content' },
  { value: 'warn', label: 'Warn' },
];

/** @deprecated Rule State removed — saved Rules apply with no activation step. */
export const RULE_STATE_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
];

export const APPEAL_CONFIRMATION_FIXED_MESSAGE =
  'I confirm that I reviewed the original decision, Appeal Messages and attachments before recording this Appeal Decision.';

export const REASON_REQUIREMENT_OPTIONS = [
  { value: 'not_required', label: 'Not Required' },
  { value: 'required', label: 'Required' },
];

export const EVIDENCE_REQUIREMENT_OPTIONS = [
  { value: 'not_required', label: 'Not Required' },
  { value: 'linked_evidence_required', label: 'Linked Evidence Required' },
];

export const CONFIRMATION_REQUIREMENT_OPTIONS = [
  { value: 'no_additional_confirmation', label: 'No Additional Confirmation' },
  { value: 'confirmation_required', label: 'Confirmation Required' },
];

export const EVIDENCE_SOURCE_OPTIONS = [
  {
    value: 'case_preserved_evidence',
    label: 'Existing preserved-evidence relationship for this case',
    reference: 'EVD-CASE-PRESERVED',
    available: true,
  },
  { value: 'case_linked_evidence', label: 'Case Linked Evidence', reference: 'EVD-CASE-LINKED', available: true },
  { value: 'target_linked_evidence', label: 'Target Linked Evidence', reference: 'EVD-TARGET-LINKED', available: true },
  { value: 'decision_form_evidence', label: 'Decision Form Evidence', reference: 'EVD-DECISION-FORM', available: true },
];

export const CONDITION_OPERATOR_OPTIONS = [
  { value: 'equals', label: 'Equals', multi: false },
  { value: 'does_not_equal', label: 'Does Not Equal', multi: false },
  { value: 'is_any_of', label: 'Is Any Of', multi: true },
  { value: 'is_not_any_of', label: 'Is Not Any Of', multi: true },
];

/** Condition Source = compatible existing case, Appeal or ticket (by module). */
export const CONDITION_SOURCE_OPTIONS = [
  { value: 'moderation', label: 'Moderation Case', module: 'moderation', reference: 'SRC-MODERATION-CASE' },
  { value: 'compliance', label: 'Compliance Case', module: 'compliance', reference: 'SRC-COMPLIANCE-CASE' },
  { value: 'fraud_security', label: 'Fraud & Security Case', module: 'fraud_security', reference: 'SRC-FS-CASE' },
  { value: 'appeals', label: 'Appeal', module: 'appeals', reference: 'SRC-APPEAL' },
  { value: 'support_tickets', label: 'Support Ticket', module: 'support_tickets', reference: 'SRC-SUPPORT-TICKET' },
];

/** Apply Restriction — durations (conditional Case Action fields). */
export const RESTRICTION_DURATION_OPTIONS = [
  { value: '24_hours', label: '24 Hours' },
  { value: '3_days', label: '3 Days' },
  { value: '7_days', label: '7 Days' },
];

/**
 * Decision Form outcome buttons are Case Actions with decisionOutcome set.
 * Action Type + Action Target on that same rule is what the button does
 * (e.g. Hide linked content, Restore linked content, Transfer to Fraud & Security).
 * No auto-chain of other rules. No Configuration — Moderation.
 */
export const DECISION_OUTCOME_OPTIONS = [
  { value: 'Confirmed', label: 'Confirmed' },
  { value: 'Not Confirmed', label: 'Not Confirmed' },
  { value: 'Unable to Determine', label: 'Unable to Determine' },
  { value: 'Transfer', label: 'Transfer' },
];

const LEGACY_DECISION_OUTCOME_MAP = {
  Approved: 'Confirmed',
  'Not Approved': 'Not Confirmed',
  'Request More Information': 'Unable to Determine',
  Approve: 'Confirmed',
  'Not Approve': 'Not Confirmed',
  confirmed: 'Confirmed',
  not_confirmed: 'Not Confirmed',
  unable_to_determine: 'Unable to Determine',
};

export function requiresRestrictionFields(actionType) {
  return normalizeActionType(actionType) === 'apply_restriction';
}

export function supportsDecisionOutcome(module, recordType) {
  return (
    normalizeCaseActionModule(module) === 'moderation' &&
    normalizeRecordType(recordType) === 'moderation_case'
  );
}

export function normalizeDecisionOutcome(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const mapped = LEGACY_DECISION_OUTCOME_MAP[raw] || raw;
  return DECISION_OUTCOME_OPTIONS.some((option) => option.value === mapped) ? mapped : '';
}

export function getDecisionOutcomeLabel(value) {
  return labelFromOptions(DECISION_OUTCOME_OPTIONS, normalizeDecisionOutcome(value)) || '—';
}

export function isFinalDecisionOutcome(outcome) {
  const normalized = normalizeDecisionOutcome(outcome);
  return (
    normalized === 'Confirmed' ||
    normalized === 'Not Confirmed' ||
    normalized === 'Transfer'
  );
}

export function isFraudSecurityTransferOutcome(outcome) {
  return normalizeDecisionOutcome(outcome) === 'Transfer';
}

export function getRestrictionDurationLabel(value) {
  return labelFromOptions(RESTRICTION_DURATION_OPTIONS, value) || value || '—';
}

export function formatRestrictionDurations(values = []) {
  const labels = (values || [])
    .map((value) => getRestrictionDurationLabel(value))
    .filter((label) => label && label !== '—');
  return labels.length ? labels.join(', ') : '—';
}

/** Condition catalogue by Condition Source (module). F&S Source/Type deferred — unavailable. */
export const CONDITION_FIELDS_BY_SOURCE = {
  moderation: [
    {
      value: 'status',
      label: 'Status',
      values: [
        { value: 'new', label: 'New' },
        { value: 'under_review', label: 'Under Review' },
        { value: 'decided', label: 'Decided' },
      ],
    },
    {
      value: 'source',
      label: 'Source',
      values: [
        { value: 'customer', label: 'Customer' },
        { value: 'business', label: 'Business' },
        { value: 'system', label: 'System' },
      ],
    },
    {
      value: 'type',
      label: 'Type',
      values: [
        { value: 'post', label: 'Post' },
        { value: 'review', label: 'Review' },
        { value: 'review_reply', label: 'Review Reply' },
        { value: 'message', label: 'Message' },
        { value: 'quote', label: 'Quote' },
        { value: 'business_profile_content', label: 'Business Profile Content' },
      ],
    },
    {
      value: 'account_type',
      label: 'Account Type',
      values: [
        { value: 'customer', label: 'Customer' },
        { value: 'business', label: 'Business' },
      ],
    },
    {
      value: 'severity',
      label: 'Severity',
      values: [
        { value: 'low', label: 'Low' },
        { value: 'medium', label: 'Medium' },
        { value: 'high', label: 'High' },
      ],
    },
  ],
  compliance: [
    {
      value: 'case_status',
      label: 'Case Status',
      values: [
        { value: 'new', label: 'New' },
        { value: 'under_review', label: 'Under Review' },
        { value: 'information_requested', label: 'Information Requested' },
        { value: 'decided', label: 'Decided' },
      ],
    },
    {
      value: 'case_type',
      label: 'Case Type',
      values: [
        { value: 'new', label: 'New' },
        { value: 'renewal', label: 'Renewal' },
        { value: 'replacement', label: 'Replacement' },
      ],
    },
    {
      value: 'type',
      label: 'Type',
      values: [
        { value: 'licence', label: 'Licence' },
        { value: 'badge', label: 'Badge' },
        { value: 'ticket', label: 'Ticket' },
      ],
    },
  ],
  fraud_security: [
    {
      value: 'status',
      label: 'Status',
      values: [
        { value: 'new', label: 'New' },
        { value: 'under_review', label: 'Under Review' },
        { value: 'awaiting_information', label: 'Awaiting Information' },
      ],
    },
    {
      value: 'account_type',
      label: 'Account Type',
      values: [
        { value: 'customer', label: 'Customer' },
        { value: 'business', label: 'Business' },
      ],
    },
    {
      value: 'priority',
      label: 'Priority',
      values: [
        { value: 'low', label: 'Low' },
        { value: 'medium', label: 'Medium' },
        { value: 'high', label: 'High' },
      ],
    },
  ],
  appeals: [
    {
      value: 'status',
      label: 'Status',
      values: [
        { value: 'submitted', label: 'Submitted' },
        { value: 'under_review', label: 'Under Review' },
        { value: 'awaiting_reply', label: 'Awaiting Reply' },
        { value: 'decided', label: 'Decided' },
        { value: 'withdrawn', label: 'Withdrawn' },
      ],
    },
    {
      value: 'source',
      label: 'Source',
      values: [
        { value: 'moderation', label: 'Moderation' },
        { value: 'compliance', label: 'Compliance' },
        { value: 'fraud_security', label: 'Fraud & Security' },
      ],
    },
    {
      value: 'account_type',
      label: 'Account Type',
      values: [
        { value: 'customer', label: 'Customer' },
        { value: 'business', label: 'Business' },
      ],
    },
  ],
  support_tickets: [
    {
      value: 'status',
      label: 'Status',
      values: [
        { value: 'new', label: 'New' },
        { value: 'in_progress', label: 'In Progress' },
        { value: 'awaiting_reply', label: 'Awaiting Reply' },
        { value: 'resolved', label: 'Resolved' },
        { value: 'closed', label: 'Closed' },
        { value: 'withdrawn', label: 'Withdrawn' },
      ],
    },
    {
      value: 'ticket_type',
      label: 'Ticket Type',
      values: [
        { value: 'general_enquiry', label: 'General Enquiry' },
        { value: 'account_issue', label: 'Account Issue' },
        { value: 'billing_subscriptions', label: 'Billing & Subscriptions' },
        { value: 'technical_issue', label: 'Technical Issue' },
        { value: 'feedback_suggestions', label: 'Feedback and Suggestions' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      value: 'submitted_as',
      label: 'Submitted As',
      values: [
        { value: 'visitor', label: 'Visitor' },
        { value: 'customer', label: 'Customer' },
        { value: 'business', label: 'Business' },
      ],
    },
    {
      value: 'priority',
      label: 'Priority',
      values: [
        { value: 'low', label: 'Low' },
        { value: 'medium', label: 'Medium' },
        { value: 'high', label: 'High' },
      ],
    },
  ],
};

const TARGET_RESOLUTION_CATALOG = [
  { module: 'moderation', recordType: 'moderation_case', value: 'current_case', label: 'Current case' },
  { module: 'moderation', recordType: 'moderation_case', value: 'case_notes', label: 'Case notes' },
  { module: 'moderation', recordType: 'moderation_case', value: 'linked_content', label: 'Content linked to case' },
  { module: 'compliance', recordType: 'compliance_case', value: 'current_case', label: 'Current case' },
  { module: 'compliance', recordType: 'compliance_case', value: 'case_notes', label: 'Case notes' },
  { module: 'fraud_security', recordType: 'fraud_security_case', value: 'current_case', label: 'Current case' },
  { module: 'fraud_security', recordType: 'fraud_security_case', value: 'case_notes', label: 'Case notes' },
  { module: 'fraud_security', recordType: 'fraud_security_case', value: 'identified_account', label: 'Affected account identified by the case' },
  { module: 'appeals', recordType: 'appeal', value: 'current_appeal', label: 'Current Appeal' },
  { module: 'support_tickets', recordType: 'support_ticket', value: 'current_ticket', label: 'Current ticket' },
  { module: 'support_tickets', recordType: 'support_ticket', value: 'case_notes', label: 'Ticket notes' },
  // Legacy targets retained for older saved samples
  { module: 'compliance', recordType: 'licence_record', value: 'current_licence_record', label: 'Current Licence Record' },
  { module: 'compliance', recordType: 'badge_record', value: 'current_badge_record', label: 'Current Badge Record' },
  { module: 'compliance', recordType: 'trade_ticket_record', value: 'current_trade_ticket_record', label: 'Current Ticket Record' },
  { module: 'moderation', recordType: 'post_moderation_case', value: 'post_linked_to_case', label: 'Post Linked to Case' },
  { module: 'fraud_security', recordType: 'fraud_case', value: 'current_case', label: 'Current Case' },
  { module: 'support_tickets', recordType: 'ticket', value: 'current_ticket', label: 'Current Ticket' },
];

const ACTION_TYPE_CATALOG = [
  { module: 'moderation', recordType: 'moderation_case', actionType: 'decision_form_save' },
  { module: 'moderation', recordType: 'moderation_case', actionType: 'transfer_to_fraud_security' },
  { module: 'moderation', recordType: 'moderation_case', actionType: 'save_internal_note' },
  // Decision Form button effect types (legacy samples / runtime)
  { module: 'moderation', recordType: 'moderation_case', actionType: 'hide' },
  { module: 'moderation', recordType: 'moderation_case', actionType: 'restore_content' },
  { module: 'moderation', recordType: 'moderation_case', actionType: 'ask_more_info' },
  { module: 'moderation', recordType: 'moderation_case', actionType: 'request_more_information' },
  { module: 'compliance', recordType: 'compliance_case', actionType: 'approve' },
  { module: 'compliance', recordType: 'compliance_case', actionType: 'not_approve' },
  { module: 'compliance', recordType: 'compliance_case', actionType: 'ask_more_info' },
  { module: 'compliance', recordType: 'compliance_case', actionType: 'transfer' },
  { module: 'compliance', recordType: 'compliance_case', actionType: 'save_internal_note' },
  { module: 'fraud_security', recordType: 'fraud_security_case', actionType: 'ask_more_info' },
  { module: 'fraud_security', recordType: 'fraud_security_case', actionType: 'sign_out_active_sessions' },
  { module: 'fraud_security', recordType: 'fraud_security_case', actionType: 'link_record' },
  { module: 'fraud_security', recordType: 'fraud_security_case', actionType: 'add_evidence' },
  { module: 'fraud_security', recordType: 'fraud_security_case', actionType: 'confirm_decision' },
  { module: 'fraud_security', recordType: 'fraud_security_case', actionType: 'save_internal_note' },
  { module: 'appeals', recordType: 'appeal', actionType: 'send_message' },
  { module: 'appeals', recordType: 'appeal', actionType: 'save_confirm_decision' },
  { module: 'appeals', recordType: 'appeal', actionType: 'save_confirm_correction' },
  { module: 'appeals', recordType: 'appeal', actionType: 'retry_action' },
  { module: 'support_tickets', recordType: 'support_ticket', actionType: 'send_reply' },
  { module: 'support_tickets', recordType: 'support_ticket', actionType: 'save_internal_note' },
  { module: 'support_tickets', recordType: 'support_ticket', actionType: 'mark_in_progress' },
  { module: 'support_tickets', recordType: 'support_ticket', actionType: 'mark_awaiting_reply' },
  { module: 'support_tickets', recordType: 'support_ticket', actionType: 'mark_resolved' },
  { module: 'support_tickets', recordType: 'support_ticket', actionType: 'return_to_in_progress' },
];

const LEGACY_MODULE_MAP = {
  posts: 'moderation',
  reviews: 'moderation',
  messages: 'moderation',
  quotes: 'moderation',
  billing_subscriptions: 'support_tickets',
};

const LEGACY_RECORD_MAP = {
  application: 'compliance_case',
  case: 'moderation_case',
  post: 'moderation_case',
  review: 'moderation_case',
  message: 'moderation_case',
  quote_request: 'moderation_case',
  licence_record: 'compliance_case',
  badge_record: 'compliance_case',
  trade_ticket_record: 'compliance_case',
  appeal_case: 'appeal',
  fraud_case: 'fraud_security_case',
  security_case: 'fraud_security_case',
  account_restriction_case: 'fraud_security_case',
  ticket: 'support_ticket',
  post_moderation_case: 'moderation_case',
  review_moderation_case: 'moderation_case',
  message_moderation_case: 'moderation_case',
  quote_moderation_case: 'moderation_case',
};

const LEGACY_ACTION_TYPE_MAP = {
  restore: 'restore_content',
  reject: 'not_approve',
  submit_rejection: 'not_approve',
  request_evidence: 'ask_more_info',
  request_more_information: 'ask_more_info',
  request_information: 'ask_more_info',
  submit_decision_form: 'decision_form_save',
  reopen: 'return_to_in_progress',
  close: 'mark_resolved',
};
const LEGACY_STATE_MAP = { draft: 'paused', inactive: 'paused' };
const LEGACY_EVIDENCE_MAP = {
  existing_evidence_required: 'linked_evidence_required',
  new_evidence_required: 'linked_evidence_required',
  evidence_or_approved_reason_required: 'linked_evidence_required',
};
const LEGACY_CONFIRMATION_MAP = {
  elevated_confirmation_required: 'confirmation_required',
};

export function labelFromOptions(options, value) {
  if (value == null || value === '') return '';
  return options.find((option) => option.value === value)?.label ?? String(value);
}

export function namesMatch(a, b) {
  return String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
}

export function operatorIsMulti(operator) {
  return CONDITION_OPERATOR_OPTIONS.find((option) => option.value === operator)?.multi === true;
}

export function normalizeCaseActionModule(value) {
  if (!value) return '';
  return LEGACY_MODULE_MAP[value] || value;
}

export function normalizeRecordType(value) {
  if (!value) return '';
  return LEGACY_RECORD_MAP[value] || value;
}

export function normalizeActionType(value) {
  if (!value) return '';
  return LEGACY_ACTION_TYPE_MAP[value] || value;
}

export function normalizeRuleState(value) {
  const mapped = LEGACY_STATE_MAP[String(value || '').toLowerCase()] || String(value || '').toLowerCase();
  return RULE_STATE_OPTIONS.some((option) => option.value === mapped) ? mapped : '';
}

export function getRecordTypeOptions(module) {
  return RECORD_TYPES_BY_MODULE[normalizeCaseActionModule(module)] || [];
}

export function getCaseActionModuleLabel(value) {
  return labelFromOptions(CASE_ACTION_MODULE_OPTIONS, normalizeCaseActionModule(value));
}

export function getRecordTypeLabel(module, value) {
  return labelFromOptions(getRecordTypeOptions(module), normalizeRecordType(value));
}

export function getActionTypeLabel(value) {
  return labelFromOptions(ACTION_TYPE_OPTIONS, normalizeActionType(value));
}

/** Saved Case Action rule for a module / record / action type (sample catalogue). */
export function getActiveCaseActionRule(module, recordType, actionType) {
  const mod = normalizeCaseActionModule(module);
  const rec = normalizeRecordType(recordType);
  const action = normalizeActionType(actionType);
  if (!mod || !rec || !action) return null;
  return (
    buildSampleCaseActionRules().find(
      (rule) =>
        normalizeCaseActionModule(rule.module) === mod &&
        normalizeRecordType(rule.recordType) === rec &&
        normalizeActionType(rule.actionType) === action
    ) || null
  );
}

/** Moderation Case Actions — Approve / Not Approve / Request More Information / Transfer / Notes. No Decision Form Save button. */
export function getModerationCaseAction(actionType) {
  return getActiveCaseActionRule('moderation', 'moderation_case', actionType);
}

export function getModerationCaseActionLabel(actionType) {
  const rule = getModerationCaseAction(actionType);
  return rule?.ruleName || getActionTypeLabel(actionType) || '';
}

/** Moderation Case Actions available on the Decision Form / case screen. */
export function getModerationDecisionCaseActions() {
  return buildSampleCaseActionRules().filter(
    (rule) =>
      normalizeCaseActionModule(rule.module) === 'moderation' &&
      normalizeRecordType(rule.recordType) === 'moderation_case' &&
      Boolean(normalizeDecisionOutcome(rule.decisionOutcome))
  );
}

const overallResultToConditionValue = (overallResult) => {
  const normalized = normalizeDecisionOutcome(overallResult) || String(overallResult || '').trim();
  if (normalized === 'Confirmed' || normalized === 'Approved') return 'confirmed';
  if (normalized === 'Not Confirmed' || normalized === 'Not Approved') return 'not_confirmed';
  if (normalized === 'Unable to Determine' || normalized === 'Request More Information') {
    return 'unable_to_determine';
  }
  if (normalized === 'Transfer') return 'transfer';
  return '';
};

export function buildModerationDecisionContext(overallResult) {
  const resultValue = overallResultToConditionValue(overallResult);
  return {
    overallResult,
    overall_result: resultValue,
    decision_recorded: overallResult ? 'yes' : 'no',
    findings_complete: overallResult ? 'yes' : 'no',
  };
}

function conditionValuesList(condition) {
  if (Array.isArray(condition?.values) && condition.values.length) return condition.values.map(String);
  if (condition?.value != null && condition.value !== '') return [String(condition.value)];
  return [];
}

export function caseActionConditionMatches(condition, context = {}) {
  const source = condition?.source;
  const field = condition?.field;
  const operator = condition?.operator || 'equals';
  const expected = conditionValuesList(condition);
  if (!source || !field || !expected.length) return false;

  let actual = '';
  if (source === 'decision_form') {
    if (field === 'overall_result') actual = String(context.overall_result || '');
    else if (field === 'decision_recorded') actual = String(context.decision_recorded || '');
    else if (field === 'findings_complete') actual = String(context.findings_complete || '');
  }
  if (!actual) return false;

  if (operator === 'equals' || operator === 'is_any_of') return expected.includes(actual);
  if (operator === 'does_not_equal' || operator === 'is_not_any_of') return !expected.includes(actual);
  return false;
}

export function caseActionMatchesDecisionContext(rule, context = {}) {
  const conditions = Array.isArray(rule?.conditions) ? rule.conditions : [];
  // Empty conditions = manual / optional only — do not auto-fire with Decision Form Save.
  if (!conditions.length) return false;
  return conditions.every((condition) => caseActionConditionMatches(condition, context));
}

/**
 * Decision Form buttons = Active Moderation Case Actions with a Decision Outcome.
 * Exactly: Confirmed · Not Confirmed · Unable to Determine · Transfer.
 * No Decision Form Save button.
 */
export function getModerationDecisionButtons() {
  const order = DECISION_OUTCOME_OPTIONS.map((option) => option.value);
  const byOutcome = new Map();
  getModerationDecisionCaseActions()
    .filter((rule) => Boolean(normalizeDecisionOutcome(rule.decisionOutcome)))
    .forEach((rule) => {
      const outcome = normalizeDecisionOutcome(rule.decisionOutcome);
      if (!byOutcome.has(outcome)) byOutcome.set(outcome, rule);
    });
  return order.map((outcome) => byOutcome.get(outcome)).filter(Boolean);
}

export function resolveModerationDecisionButtonClick(actionRule) {
  const rule = normalizeCaseActionForm(actionRule || {});
  const outcome = normalizeDecisionOutcome(rule.decisionOutcome);
  if (!rule.id || !outcome) {
    return {
      isFinal: false,
      outcome: null,
      primaryAction: null,
      appliedActions: [],
      context: buildModerationDecisionContext(null),
    };
  }

  return {
    isFinal: isFinalDecisionOutcome(outcome),
    outcome,
    primaryAction: rule,
    // Effect is this rule only (Action Type + Target). No chained rules.
    appliedActions: [rule],
    context: buildModerationDecisionContext(outcome),
  };
}

/** @deprecated — use resolveModerationDecisionButtonClick(actionRule). */
export function resolveModerationDecisionSave(overallResult) {
  const button = getModerationDecisionButtons().find(
    (rule) => normalizeDecisionOutcome(rule.decisionOutcome) === overallResult
  );
  if (button) return resolveModerationDecisionButtonClick(button);
  const saveAction = getModerationCaseAction('decision_form_save');
  return {
    isFinal: false,
    outcome: null,
    primaryAction: saveAction,
    appliedActions: saveAction ? [saveAction] : [],
    context: buildModerationDecisionContext(null),
  };
}

export function getActionTypesForModuleRecord(module, recordType) {
  if (!module || !recordType) return [];
  const mod = normalizeCaseActionModule(module);
  const rec = normalizeRecordType(recordType);
  const keys = new Set(
    ACTION_TYPE_CATALOG.filter((row) => row.module === mod && row.recordType === rec).map((row) => row.actionType),
  );
  return ACTION_TYPE_OPTIONS.filter((option) => keys.has(option.value));
}

export function getTargetResolutionsForRule(module, recordType) {
  if (!module || !recordType) return [];
  const mod = normalizeCaseActionModule(module);
  const rec = normalizeRecordType(recordType);
  const seen = new Set();
  return TARGET_RESOLUTION_CATALOG.filter((row) => {
    if (row.module !== mod || row.recordType !== rec) return false;
    if (seen.has(row.value)) return false;
    seen.add(row.value);
    return true;
  }).map(({ value, label }) => ({ value, label }));
}

export function getActionTargetLabel(module, recordType, value) {
  return labelFromOptions(getTargetResolutionsForRule(module, recordType), value);
}

export function getEvidenceSourceDefinition(value) {
  return EVIDENCE_SOURCE_OPTIONS.find((option) => option.value === value) || null;
}

export function getEvidenceSourcesForScope() {
  return EVIDENCE_SOURCE_OPTIONS.filter((option) => option.available !== false);
}

export function isEvidenceSourceAvailable(value) {
  if (!value) return true;
  const source = getEvidenceSourceDefinition(value);
  return Boolean(source && source.available !== false);
}

export function getConditionSourcesForScope(module) {
  const mod = normalizeCaseActionModule(module);
  if (!mod) return CONDITION_SOURCE_OPTIONS;
  return CONDITION_SOURCE_OPTIONS.filter((option) => option.module === mod);
}

export function getAvailableConditionFieldsForScope(module) {
  const sources = getConditionSourcesForScope(module);
  return sources.flatMap((source) =>
    getConditionFieldsForSource(source.value).map((field) => ({
      ...field,
      source: source.value,
      sourceLabel: source.label,
    })),
  );
}

export function getConditionFieldsForSource(source) {
  return CONDITION_FIELDS_BY_SOURCE[source] || [];
}

export function getConditionValueOptions(source, field) {
  return getConditionFieldsForSource(source).find((option) => option.value === field)?.values || [];
}

export function getConditionSourceLabel(value) {
  return labelFromOptions(CONDITION_SOURCE_OPTIONS, value);
}

export function getConditionFieldLabel(source, field) {
  return labelFromOptions(getConditionFieldsForSource(source), field);
}

export function getConditionOperatorLabel(value) {
  return labelFromOptions(CONDITION_OPERATOR_OPTIONS, value);
}

export function getConditionValueLabel(source, field, value) {
  return labelFromOptions(getConditionValueOptions(source, field), value) || String(value || '');
}

export function getRuleStateLabel(value) {
  return labelFromOptions(RULE_STATE_OPTIONS, normalizeRuleState(value));
}

export function getReasonRequirementLabel(value) {
  return labelFromOptions(REASON_REQUIREMENT_OPTIONS, value);
}

export function getEvidenceRequirementLabel(value) {
  return labelFromOptions(EVIDENCE_REQUIREMENT_OPTIONS, value);
}

export function getConfirmationRequirementLabel(value) {
  return labelFromOptions(CONFIRMATION_REQUIREMENT_OPTIONS, value);
}

export function getEvidenceSourceLabel(value) {
  return labelFromOptions(EVIDENCE_SOURCE_OPTIONS, value);
}

export function getEvidenceSourceReference(value) {
  return getEvidenceSourceDefinition(value)?.reference || '';
}

export function getConditionSourceReference(value) {
  return CONDITION_SOURCE_OPTIONS.find((option) => option.value === value)?.reference || '';
}

export function getReferenceAvailabilityLabel(evidenceSource) {
  if (!evidenceSource) return '—';
  return isEvidenceSourceAvailable(evidenceSource) ? 'Available' : 'Unavailable';
}

export function applyCaseActionSelectionChange(form, field, value) {
  const next = { ...form, [field]: value };
  if (field === 'module') {
    const recordTypes = getRecordTypeOptions(next.module).map((option) => option.value);
    if (next.recordType && !recordTypes.includes(next.recordType)) next.recordType = '';
    next.conditions = [];
  }
  if (field === 'module' || field === 'recordType') {
    const actionTypes = getActionTypesForModuleRecord(next.module, next.recordType).map((option) => option.value);
    if (next.actionType && !actionTypes.includes(next.actionType)) next.actionType = '';
    const targets = getTargetResolutionsForRule(next.module, next.recordType).map((option) => option.value);
    if (next.actionTarget && !targets.includes(next.actionTarget)) next.actionTarget = '';
  }
  return applyCaseActionDerivedFields(next);
}

export function requiresEvidenceSource(evidenceRequirement) {
  return evidenceRequirement === 'linked_evidence_required';
}

export function requiresConfirmationMessage(confirmationRequirement) {
  return confirmationRequirement === 'confirmation_required';
}

export function formatRequirementPreview(requirement) {
  return requirement === 'required' || requirement === 'linked_evidence_required' || requirement === 'confirmation_required'
    ? 'Required'
    : 'Not Required';
}

export function buildCaseActionScopeKey(rule) {
  return [
    normalizeCaseActionModule(rule.module) || '',
    normalizeRecordType(rule.recordType) || '',
    normalizeActionType(rule.actionType) || '',
    rule.actionTarget || rule.actionTargetResolution || '',
  ].join('|');
}

function uniqueSorted(values = []) {
  return [...new Set((values || []).map((v) => String(v || '').trim()).filter(Boolean))].sort();
}

function normalizeOperatorForCompare(operator, values) {
  const vals = uniqueSorted(values);
  if (operator === 'equals' || (operator === 'is_any_of' && vals.length === 1)) {
    return { operator: 'equals', values: vals.slice(0, 1) };
  }
  if (operator === 'does_not_equal' || (operator === 'is_not_any_of' && vals.length === 1)) {
    return { operator: 'does_not_equal', values: vals.slice(0, 1) };
  }
  return { operator, values: vals };
}

function conditionFingerprint(condition) {
  const normalized = normalizeOperatorForCompare(condition.operator, condition.values || []);
  return [
    condition.source || '',
    condition.field || '',
    normalized.operator,
    normalized.values.join(','),
  ].join('|');
}

function sortedConditionFingerprints(conditions = []) {
  return [...(conditions || []).map(conditionFingerprint)].sort();
}

function requirementsKey(rule) {
  return [
    rule.reasonRequirement || '',
    rule.evidenceRequirement || '',
    rule.evidenceSource || '',
    rule.confirmationRequirement || '',
    String(rule.confirmationMessage || '').trim(),
  ].join('|');
}

function sameConditionCoverage(a, b) {
  const left = sortedConditionFingerprints(a?.conditions);
  const right = sortedConditionFingerprints(b?.conditions);
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

function hasNoConditions(rule) {
  return !Array.isArray(rule?.conditions) || rule.conditions.length === 0;
}

/** Exact duplicate: same scope, condition coverage and submission requirements. */
export function findDuplicateCaseActionRule(rules, candidate, excludeId) {
  const key = buildCaseActionScopeKey(candidate);
  if (!candidate.module || !candidate.recordType || !candidate.actionType || !(candidate.actionTarget || candidate.actionTargetResolution)) {
    return null;
  }
  const req = requirementsKey(candidate);
  return (rules || []).find((rule) => {
    if (rule.id === excludeId) return false;
    if (buildCaseActionScopeKey(rule) !== key) return false;
    if (!sameConditionCoverage(rule, candidate)) return false;
    return requirementsKey(rule) === req;
  }) || null;
}

/**
 * Overlap: same scope and the same eligible request could match both Rules.
 * No Conditions covers the whole scope. Identical condition sets also overlap.
 */
export function findOverlappingCaseActionRule(rules, candidate, excludeId) {
  const key = buildCaseActionScopeKey(candidate);
  if (!key || key.endsWith('||') || key.split('|').some((part, i) => i < 4 && !part)) return null;
  return (rules || []).find((rule) => {
    if (rule.id === excludeId) return false;
    if (buildCaseActionScopeKey(rule) !== key) return false;
    if (hasNoConditions(candidate) || hasNoConditions(rule)) return true;
    return sameConditionCoverage(rule, candidate);
  }) || null;
}

/** Conflict: overlapping coverage with different submission requirements. */
export function findConflictingCaseActionRule(rules, candidate, excludeId) {
  const overlap = findOverlappingCaseActionRule(rules, candidate, excludeId);
  if (!overlap) return null;
  if (requirementsKey(overlap) === requirementsKey(candidate)) return null;
  return overlap;
}

export function nextCaseActionRuleId(allRules) {
  const nums = (allRules || []).map((rule) => {
    const match = String(rule.id || '').match(/^ACT-(\d+)$/i);
    return match ? parseInt(match[1], 10) : 0;
  });
  const max = nums.length ? Math.max(...nums) : 0;
  return `ACT-${String(max + 1).padStart(4, '0')}`;
}

export function createEmptyCondition() {
  return { id: `cond-${Date.now()}`, source: '', field: '', operator: '', values: [] };
}

export function createEmptyCaseActionForm() {
  return {
    id: '',
    ruleName: '',
    module: '',
    recordType: '',
    actionType: '',
    actionTarget: '',
    decisionOutcome: '',
    reasonRequirement: 'not_required',
    evidenceRequirement: 'not_required',
    evidenceSource: '',
    confirmationRequirement: 'no_additional_confirmation',
    confirmationMessage: '',
    conditions: [],
    changeReason: '',
    dateAdded: '',
    addedBy: '',
    lastUpdated: '',
    lastUpdatedBy: '',
  };
}

function normalizeCondition(raw) {
  const values = Array.isArray(raw?.values)
    ? raw.values.filter(Boolean)
    : raw?.comparisonValue
      ? [raw.comparisonValue]
      : raw?.value
        ? [raw.value]
        : [];
  return {
    id: raw?.id || `cond-${Math.random().toString(36).slice(2, 8)}`,
    source: raw?.source || raw?.conditionSource || '',
    field: raw?.field || raw?.conditionField || '',
    operator: raw?.operator || '',
    values,
  };
}

export function applyCaseActionDerivedFields(form) {
  const next = { ...form };
  if (!requiresEvidenceSource(next.evidenceRequirement)) next.evidenceSource = '';
  if (!requiresConfirmationMessage(next.confirmationRequirement)) next.confirmationMessage = '';
  if (
    next.module === 'appeals'
    && next.actionType === 'save_confirm_decision'
    && next.confirmationRequirement === 'confirmation_required'
    && !String(next.confirmationMessage || '').trim()
  ) {
    next.confirmationMessage = APPEAL_CONFIRMATION_FIXED_MESSAGE;
  }
  if (!supportsDecisionOutcome(next.module, next.recordType)) {
    next.decisionOutcome = '';
  } else {
    next.decisionOutcome = normalizeDecisionOutcome(next.decisionOutcome);
  }
  return next;
}

export function normalizeCaseActionForm(raw = {}) {
  const module = normalizeCaseActionModule(raw.module);
  const recordType = normalizeRecordType(raw.recordType);
  const actionType = normalizeActionType(raw.actionType);
  let evidenceRequirement = raw.evidenceRequirement || 'not_required';
  evidenceRequirement = LEGACY_EVIDENCE_MAP[evidenceRequirement] || evidenceRequirement;
  let confirmationRequirement = raw.confirmationRequirement || 'no_additional_confirmation';
  confirmationRequirement = LEGACY_CONFIRMATION_MAP[confirmationRequirement] || confirmationRequirement;
  const derived = applyCaseActionDerivedFields({
    ...createEmptyCaseActionForm(),
    ...raw,
    module,
    recordType,
    actionType,
    actionTarget: raw.actionTarget || raw.actionTargetResolution || '',
    decisionOutcome: raw.decisionOutcome || '',
    reasonRequirement: raw.reasonRequirement || 'not_required',
    evidenceRequirement,
    evidenceSource: raw.evidenceSource || '',
    confirmationRequirement,
    confirmationMessage: raw.confirmationMessage || '',
    conditions: Array.isArray(raw.conditions) ? raw.conditions.map(normalizeCondition) : [],
  });
  return {
    ...createEmptyCaseActionForm(),
    ...raw,
    ...derived,
    ruleName: raw.ruleName || '',
    changeReason: raw.changeReason || '',
    dateAdded: raw.dateAdded || raw.createdOn || '',
    addedBy: raw.addedBy || raw.createdBy || '',
    lastUpdated: raw.lastUpdated || raw.modifiedOn || '',
    lastUpdatedBy: raw.lastUpdatedBy || raw.modifiedBy || '',
  };
}

export function formatConditionLine(condition) {
  const source = getConditionSourceLabel(condition.source);
  const field = getConditionFieldLabel(condition.source, condition.field);
  const operator = getConditionOperatorLabel(condition.operator);
  const values = (condition.values || []).map((value) => getConditionValueLabel(condition.source, condition.field, value));
  return [source, field, operator, values.join(', ')].filter(Boolean).join(' · ');
}

export function formatConditionsPreview(conditions = []) {
  if (!conditions.length) return { text: 'No Conditions', extraCount: 0, lines: [] };
  const lines = conditions.map(formatConditionLine);
  if (lines.length === 1) return { text: lines[0], extraCount: 0, lines };
  return { text: `${lines.length} Conditions`, extraCount: 0, lines };
}

export function formatCaseActionDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const day = date.getDate();
  const month = date.toLocaleString('en-GB', { month: 'short' });
  const year = date.getFullYear();
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const suffix = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12 || 12;
  return `${day} ${month} ${year}, ${hours}:${minutes} ${suffix}`;
}

export function sortCaseActionRules(rules = []) {
  return [...rules].sort((a, b) => {
    const aTime = new Date(a.lastUpdated || a.dateAdded || 0).getTime();
    const bTime = new Date(b.lastUpdated || b.dateAdded || 0).getTime();
    return bTime - aTime;
  });
}

function actorFields(stamp, name) {
  return {
    addedBy: name,
    lastUpdatedBy: name,
    dateAdded: stamp,
    lastUpdated: stamp,
  };
}

export function buildSampleCaseActionRules() {
  const samples = [
    {
      id: 'ACT-0001',
      ruleName: 'Confirm Fraud Decision',
      module: 'fraud_security',
      recordType: 'fraud_security_case',
      actionType: 'confirm_decision',
      actionTarget: 'current_case',
      reasonRequirement: 'required',
      evidenceRequirement: 'linked_evidence_required',
      evidenceSource: 'case_preserved_evidence',
      confirmationRequirement: 'no_additional_confirmation',
      confirmationMessage: '',
      conditions: [],
      changeReason: 'Require linked case evidence before confirming the decision.',
      ...actorFields('2026-08-16T10:35:00.000Z', 'Olivia Brown'),
    },
    {
      id: 'ACT-0002',
      ruleName: 'Confirm Appeal Decision',
      module: 'appeals',
      recordType: 'appeal',
      actionType: 'save_confirm_decision',
      actionTarget: 'current_appeal',
      reasonRequirement: 'required',
      evidenceRequirement: 'not_required',
      evidenceSource: '',
      confirmationRequirement: 'confirmation_required',
      confirmationMessage: APPEAL_CONFIRMATION_FIXED_MESSAGE,
      conditions: [],
      changeReason: 'Require reason and confirmation before recording an Appeal Decision.',
      ...actorFields('2026-08-16T10:20:00.000Z', 'Olivia Brown'),
    },
    {
      id: 'ACT-0003',
      ruleName: 'Account Ticket Reply',
      module: 'support_tickets',
      recordType: 'support_ticket',
      actionType: 'send_reply',
      actionTarget: 'current_ticket',
      reasonRequirement: 'not_required',
      evidenceRequirement: 'not_required',
      evidenceSource: '',
      confirmationRequirement: 'no_additional_confirmation',
      confirmationMessage: '',
      conditions: [
        {
          source: 'support_tickets',
          field: 'ticket_type',
          operator: 'equals',
          values: ['account_issue'],
        },
      ],
      changeReason: 'Allow staff reply for Account Issue tickets.',
      ...actorFields('2026-08-16T09:35:00.000Z', 'Olivia Brown'),
    },
    {
      id: 'ACT-0004',
      ruleName: 'Technical Ticket Reply',
      module: 'support_tickets',
      recordType: 'support_ticket',
      actionType: 'send_reply',
      actionTarget: 'current_ticket',
      reasonRequirement: 'not_required',
      evidenceRequirement: 'not_required',
      evidenceSource: '',
      confirmationRequirement: 'no_additional_confirmation',
      confirmationMessage: '',
      conditions: [
        {
          source: 'support_tickets',
          field: 'ticket_type',
          operator: 'equals',
          values: ['technical_issue'],
        },
        {
          source: 'support_tickets',
          field: 'priority',
          operator: 'equals',
          values: ['high'],
        },
      ],
      changeReason: 'Allow staff reply for High-priority Technical Issue tickets.',
      ...actorFields('2026-08-16T09:40:00.000Z', 'Olivia Brown'),
    },
    // Runtime Decision Form button Rules (not Table 1 examples)
    {
      id: 'ACT-0005',
      ruleName: 'Confirmed',
      module: 'moderation',
      recordType: 'moderation_case',
      actionType: 'hide',
      actionTarget: 'linked_content',
      decisionOutcome: 'Confirmed',
      reasonRequirement: 'required',
      evidenceRequirement: 'not_required',
      confirmationRequirement: 'no_additional_confirmation',
      conditions: [],
      changeReason: 'Decision Form Confirmed outcome.',
      ...actorFields('2026-08-16T11:10:00.000Z', 'Olivia Brown'),
    },
    {
      id: 'ACT-0006',
      ruleName: 'Not Confirmed',
      module: 'moderation',
      recordType: 'moderation_case',
      actionType: 'restore_content',
      actionTarget: 'linked_content',
      decisionOutcome: 'Not Confirmed',
      reasonRequirement: 'required',
      evidenceRequirement: 'not_required',
      confirmationRequirement: 'no_additional_confirmation',
      conditions: [],
      changeReason: 'Decision Form Not Confirmed outcome.',
      ...actorFields('2026-08-16T11:15:00.000Z', 'Olivia Brown'),
    },
    {
      id: 'ACT-0007',
      ruleName: 'Unable to Determine',
      module: 'moderation',
      recordType: 'moderation_case',
      actionType: 'ask_more_info',
      actionTarget: 'current_case',
      decisionOutcome: 'Unable to Determine',
      reasonRequirement: 'required',
      evidenceRequirement: 'not_required',
      confirmationRequirement: 'no_additional_confirmation',
      conditions: [],
      changeReason: 'Decision Form Unable to Determine progress finding.',
      ...actorFields('2026-08-16T11:18:00.000Z', 'Olivia Brown'),
    },
    {
      id: 'ACT-0008',
      ruleName: 'Transfer',
      module: 'moderation',
      recordType: 'moderation_case',
      actionType: 'transfer_to_fraud_security',
      actionTarget: 'current_case',
      decisionOutcome: 'Transfer',
      reasonRequirement: 'required',
      evidenceRequirement: 'not_required',
      confirmationRequirement: 'confirmation_required',
      confirmationMessage: 'Are you sure you want to transfer this case to Fraud & Security?',
      conditions: [],
      changeReason: 'Decision Form Transfer button.',
      ...actorFields('2026-08-16T11:20:00.000Z', 'Olivia Brown'),
    },
  ];
  return samples.map((rule) => normalizeCaseActionForm(rule));
}

/** @deprecated aliases */
export const ACTION_AVAILABLE_TO_OPTIONS = [];
export const ACTION_BASIS_TYPE_OPTIONS = [];
export const AVAILABILITY_TYPE_OPTIONS = [];
export const CONFIRMATION_METHOD_OPTIONS = [];
export const APPROVER_RESOLUTION_OPTIONS = [];
export const REQUIRED_APPROVER_ROLE_OPTIONS = [];
export const EXPIRY_UNIT_OPTIONS = [];
export const ACTION_RESULT_BY_TYPE = {};

export function generateNextRuleId(existingRules) {
  return nextCaseActionRuleId(existingRules);
}

export function buildSampleActionRules() {
  return buildSampleCaseActionRules();
}

export function loadCaseActionRules() {
  return buildSampleCaseActionRules();
}

export function persistCaseActionRules() {}

export function getCaseActionRuleById(ruleId) {
  return buildSampleCaseActionRules().find((rule) => rule.id === ruleId) || null;
}

export function normalizeCaseActionStatus(status) {
  return normalizeRuleState(status) || 'paused';
}

export function getActionResultForType() {
  return null;
}

export function getActionResultLabel() {
  return '';
}

export function getAvailabilityConditionsForRule() {
  return [];
}

export function getActionBasisSourcesForRule() {
  return [];
}

export function getConfirmationMethodsForRequirement() {
  return [];
}

export function formatConfirmationMethodColumn(rule) {
  return formatRequirementPreview(normalizeCaseActionForm(rule).confirmationRequirement);
}
